import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { DailyWorkflowBar } from '../components/dailyWorkflowBar/DailyWorkflowBar';
import { DropClassificationPanel, dropPanelCollapsedKey } from '../components/dropClassification/DropClassificationPanel';
import { DropReviewTable, DropTableTab } from '../components/dropClassification/DropReviewTable';
import { HubChangesTable, readOpenTabHint } from '../components/hubChangesTable/HubChangesTable';
import { PriceLookupFocus, clearPriceLookupFocus, readPriceLookupFocus } from '../utils/priceLookupFocus';
import { buildHubSelection, hubRowStatus } from '../components/hubChangesTable/hubRows';
import { allDropGroupCards, groupDropCards } from '../components/dropClassification/dropGroups';
import { PriceViewBar } from '../components/priceViews/PriceViewBar';
import {
  PRICE_VIEW_LABEL, PRICE_VIEW_PAGE, type PriceView, type PriceViewCounts, type PriceViewPage,
  cardMatchesView, countCardViews, hubTabOfView, lastCollectedAt, readPriceView, viewOfHubTab, writePriceView,
} from '../components/priceViews/priceViews';
import { JobModal } from '../components/modal/JobModal';
import { DropReviewModal } from '../components/modal/DropReviewModal';
import { MorningSummaryCard } from '../components/morningSummary/MorningSummaryCard';
import { useCardPage } from '../contexts/CardPageContext';
import { useCardEdit } from '../contexts/CardEditContext';
import { useHubChanges } from '../hooks/UseHubChanges';
import { usePriceSummary } from '../hooks/UsePriceSummary';
import dayjs from 'dayjs';
import { useInlineLoadError } from '../hooks/UseAsyncData';
import { AppLoadError } from '../components/common/AppLoadError';
import { LAYOUT } from '../constants/constants';
import { useStorage } from '../contexts/StorageContext';
import { Card } from '../types/card';
import { ApplyCurrentApplied, isPendingApplied } from '../types/hubChanges';

// 헤더(56) + JobBar(44) + DashboardLayout 상하 패딩(24*2) + DailyWorkflowBar(~100) + 탭 바·설명(~64) + 페이지 줄(~40) +
// 섹션 간 여백. "매장가 요청" 은 여기에 DropClassificationPanel(~200) · 반영 바(~60) · 표 위 도구 줄(~80)이 더해진다.
const TABLE_SCROLL_Y = `calc(100vh - ${LAYOUT.headerHeight + LAYOUT.jobBarHeight + 48 + 100 + 64 + 40 + 40}px)`;
// "매장가 요청" 표는 페이지와 함께 스크롤되고 선택 줄 · 머리줄이 화면 위에 붙는다(감사 card-controltower#326) — 이 높이는 가상 스크롤
// (500줄 이상 페이지)에만 쓰여, 표를 화면 위까지 올렸을 때 헤더 · 진행 바 · 선택 줄(~48) · 머리줄(~40) · 아래 페이지 줄(~48)을
// 뺀 나머지를 다 쓴다.
const HUB_TABLE_SCROLL_Y = `calc(100vh - ${LAYOUT.headerHeight + LAYOUT.jobBarHeight + 48 + 40 + 48}px)`;

/**
 * 같은 화면 논리를 두 메뉴로 나눠 보인다(감사 card-controltower#117, 세 화면 재설계 F2 #452 — 사장 결정 2026-10-29).
 *   - steps("가격 조회"): ①②③ 세 단계 바 + 시세 분류 요약(접기) + 하락 확인 현황 + 카드 탭 표. 분류는 ① 의 결과이고 ② 의
 *     입력이라 이 화면의 일이다 — 요약 카드의 "목록 보기" 는 같은 화면의 탭 전환.
 *   - storePrice("매장 가격 요청"): PriceHub 변경 표(네이버 가격 → <매장> 가격 요청 · 고른 카드만 ③ 스토어 반영).
 */
export type DailyWorkflowVariant = 'steps' | 'storePrice';

/**
 * "가격 조회" — ① 가격 받기 → (자동) 타 샵 시세 대조 분류 → ② 하락 검토 → ③ 스토어 반영.
 *
 * 하락 카드는 dropBucket으로 3분류(시세 범위 내 / 확인 필요 / 참조 없음)하고, 가격 상승과
 * 함께 하나의 탭 전환 표(DropReviewTable)로 목록을 보여준다("하락 검토 개선안").
 * 확인 필요(SUSPECT)만 재설계된 모달로 한 건씩 검토하고, 나머지는 읽기 전용 확인 목록이다.
 *
 * 다섯째 탭 "PriceHub 변경"(2026-10-21, 매장별 판매가 3단계) — PriceHub 매장 판매가 ≠ 네이버 가격인 카드
 * 전부와 매장 직접 변경 알림. 이 탭에서는 사용자가 이 화면에서 체크한 카드만 ③ 으로 반영한다(다른 탭의 ③ 은
 * 그대로 전체, 선택은 화면 상태 — 감사 #330). 매장 직접 변경 알림(확정 대기) 카드는 체크되지만 "→ PriceHub 매장가" 요청 묶음에만 들어가고 ③ 에서는
 * 빠진다(감사 #100). 오늘 ① 조회 전이면 하락 줄은 ③ 에서만 빼고(체크 · 요청은 된다, 감사 #72 · #332) 상승만 보낸다.
 */
export const DailyWorkflowPage: React.FC<{ variant?: DailyWorkflowVariant }> = ({ variant = 'steps' }) => {
  const storePrice = variant === 'storePrice';
  const {
    cards, reviewOpen, setReviewOpen, filters, selection,
    resultJob, resultModalOpen, setResultModalOpen, goToDropReview,
  } = useCardPage();
  const edit = useCardEdit();
  // 세 단계 화면은 PriceHub 변경 목록을 쓰지 않는다 — 들어올 때 · 잡 끝마다 받지 않는다(Opus 리뷰 PR #53 낮음).
  const hub = useHubChanges(storePrice);
  // "PriceHub 변경" 새로고침은 카드 캐시도 함께 다시 받는다 — 표의 상승 · 하락은 받은 PriceHub 목록 위에 카드 캐시를
  // 덮어 정하는데(overlayLocalCards), 다른 작업자가 바꾼 뒤 캐시가 옛 UNCHANGED 로 남아 있으면 새로 온 상승 줄이
  // 사라졌다(2026-10-22 코덱 전체 점검, 감사 card-controltower#107).
  const { reload: reloadHub } = hub;
  const { reload: reloadCards } = cards;
  const { reloadWithCards: reloadHubWithCards } = hub;
  const reloadHubAndCards = useCallback(() => {
    // 둘을 따로 받는다 — 카드 조회 성공에만 PriceHub 재조회를 묶으면 카드 API 가 계속 실패할 때 정상인 PriceHub 목록도 못 받았다
    // (코덱 리뷰 #209-5, 감사 #285). reloadWithCards 는 뒤따르는 카드 재조회 한 번을 건너뛰어 같은 요청이 두 번 나가지 않는다.
    if (storePrice) {
      void reloadHubWithCards(reloadCards);
    } else {
      void reloadCards();
      void reloadHub();
    }
  }, [reloadHub, reloadHubWithCards, reloadCards, storePrice]);

  // 이 화면은 재조회 실패를 인라인 배너로 직접 그린다 — 마운트 동안만 토스트를 끈다.
  // 카드 목록 훅은 여러 화면이 공유하므로, 벗어나면 다시 켜져 다른 화면은 예전처럼 토스트로
  // 알린다(2026-10-21 회귀 수정).
  useInlineLoadError(cards.setInlineError);

  // 첫 조회부터 실패해 보여 줄 카드가 없는 상태. 예전엔 `cards.length === 0` 으로 판단해서
  // 진짜 0건이 정상 응답인 경우 재조회 실패가 전체 오류 화면으로 둔갑했다 — "성공한 적이
  // 있는지"로 판단한다(BuybackPage 와 같은 기준, 2026-10-21 리뷰).
  const failedEmpty = !!cards.error && !cards.hasLoadedOnce;

  const [tableTab, setTableTab] = useState<DropTableTab>('suspect');
  // 매장 가격 요청 [반영] 에서 온 카드(코덱 리뷰 #869) — 한 번짜리 힌트를 처음 그릴 때 읽고(가격 조회만) 마운트 뒤 지운다.
  const [lookupFocus, setLookupFocus] = useState<PriceLookupFocus | null>(() => (storePrice ? null : readPriceLookupFocus()));
  useEffect(() => {
    if (!storePrice) clearPriceLookupFocus();
    // 처음 한 번
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 저장 필터 칩(가격 목록 개편 PR 3, 감사 card-controltower#697) — 고른 칩은 주소의 ?view= 에 남는다(링크 공유 · 새로고침).
  // 처음 값: 주소의 보기가 이 화면 것이면 그것, 아니면(매장 가격 요청) 판매가 결정이 남긴 한 번짜리 탭 힌트(감사 #505)의 칩.
  const pageKey: PriceViewPage = storePrice ? 'storePrice' : 'daily';
  const [view, setViewState] = useState<PriceView | null>(() => {
    const v = readPriceView();
    if (v && PRICE_VIEW_PAGE[v] === pageKey) return v;
    return storePrice ? viewOfHubTab(readOpenTabHint()) : null;
  });
  const setView = useCallback((v: PriceView | null) => {
    setViewState(v);
    writePriceView(v);
  }, []);
  // 처음 그린 뒤 주소를 화면의 보기와 맞춘다 — 다른 화면 칩이 주소에 남아 있으면 지운다. 화면을 떠나면 지운다(다른 메뉴 주소에 남지 않게).
  useEffect(() => {
    writePriceView(view);
    return () => writePriceView(null);
    // 처음 한 번 · 떠날 때 한 번
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { isLoggedIn, username, superStore } = useStorage();
  // 분류 요약 카드의 "목록 보기" — 같은 화면의 그 탭으로 바꾸고 표로 내려간다(F2, 예전엔 다른 메뉴에서 sessionStorage 로 넘겼다).
  const tableAnchorRef = useRef<HTMLDivElement | null>(null);
  const showTab = useCallback((tab: DropTableTab) => {
    setTableTab(tab);
    const el = tableAnchorRef.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  // 분류 요약의 '하락 검토 N건 목록' → '하락 검토' 칩을 켜고 표로 내려간다(개편 §2-3 '대시보드 연결').
  const showDrops = useCallback(() => {
    setView('drop');
    const el = tableAnchorRef.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [setView]);

  // 진입 시 다른 화면(카드 관리)에서 걸어둔 필터가 이 화면 표에 그대로 물려오지 않도록
  // 전부 초기화한다. 이 화면엔 그 필터들을 보여주거나 끌 UI 자체가 없다.
  useEffect(() => {
    filters.setQuickFilter('DROP');
    filters.setTypeFilter('ALL');
    filters.setExpansionFilter('ALL');
    filters.setRarityFilter([]);
    filters.setSaleStatusFilter('ALL');
    filters.setSearch('');
    filters.setNeedsReviewOnly(false);
    return () => {
      filters.setQuickFilter('ALL');
      selection.clearSelection();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── PriceHub 변경 표("매장 가격 요청") — 요청 전용(세 화면 재설계 F3 #453): 네이버 반영(③)은 가격 조회 화면에서만 한다.
  // 받은 뒤 바뀐 카드(하락 검토의 적용가 수정 · 유지 등)는 목록을 다시 받지 않아도 화면 값으로 덮는다.
  const cardsById = useMemo(() => new Map(cards.cards.map((c) => [c.id, c])), [cards.cards]);
  const hubModel = useMemo(
    () => buildHubSelection(hub.data?.items ?? [], cardsById),
    [hub.data, cardsById],
  );

  // 칩 건수 — 필터 전 전체를 화면에서 센다(결정 D8). 카드 칩 셋은 가격 조회 표 네 탭의 카드(표에 없는 카드를 세지 않게), PriceHub 칩
  // 둘은 매장 가격 요청 목록의 상태(CT 에 없는 상품 포함). 가격 조회 화면은 PriceHub 목록을 받지 않으므로(Opus 리뷰 PR #53) 그 둘은
  // 서버 요약(`GET /api/cards/price-summary`, 감사 #745 · 백엔드 PR #93 — 같은 분류 규칙, 30초 캐시)으로 채운다. 옛 서버(404) ·
  // 오류 · PriceHub 목록을 못 받은 요약이면 지금처럼 비운다 — 눌러서 매장 가격 요청으로 가면 보인다.
  const priceSummary = usePriceSummary(!storePrice);
  const summary = priceSummary.summary;
  const dropTableCards = useMemo(() => allDropGroupCards(groupDropCards(cards.cards)), [cards.cards]);
  const viewCounts: PriceViewCounts = useMemo(() => {
    // 카드를 한 번도 받지 못했으면(첫 로딩 · 첫 조회 실패) 카드 칩 건수는 모른다 — 0 이 아니라 비운다(시우 검수 2026-11-02 낮음).
    const cardCounts = cards.hasLoadedOnce
      ? countCardViews(dropTableCards, edit.pendingConfirmIds)
      : { held: null, drop: null, syncFailed: null };
    let pending: number | null = null;
    let excluded: number | null = null;
    if (storePrice && hub.data) {
      pending = 0; excluded = 0;
      for (const r of hubModel.rows) {
        const s = hubRowStatus(r);
        if (s === 'pending') pending += 1;
        else if (s === 'blocked') excluded += 1;
      }
    } else if (!storePrice && summary?.pricehubAvailable) {
      // PriceHub 목록을 못 받은 요약(pricehubAvailable=false)은 로컬 부분만이라 확정 대기가 늘 0 으로 보인다 — 모름으로 둔다.
      pending = summary.requests.pending;
      excluded = summary.requests.blocked;
    }
    return { ...cardCounts, pending, excluded };
  }, [cards.hasLoadedOnce, dropTableCards, edit.pendingConfirmIds, storePrice, hub.data, hubModel.rows, summary]);
  // 요약 건수의 기준 시각 · 한계(칩 뜻 줄에만 — 칩 글은 그대로).
  const countNotes = useMemo((): Partial<Record<PriceView, string>> | undefined => {
    if (storePrice || !summary) return undefined;
    let note: string;
    if (!summary.pricehubAvailable) note = 'PriceHub 목록을 받지 못해 건수를 모릅니다';
    else {
      const at = summary.generatedAt ? dayjs(summary.generatedAt) : null;
      const base = at && at.isValid() ? `${at.format('HH:mm:ss')} 기준` : '서버 요약 기준';
      note = summary.pricehubTruncated ? `${base} — PriceHub 목록이 잘려 실제보다 적을 수 있습니다` : base;
    }
    return { pending: note, excluded: note };
  }, [storePrice, summary]);
  // 가격 조회 표의 칩 조건(보류 · 하락 검토 · 동기화 실패).
  const { pendingConfirmIds } = edit;
  const dropViewFilter = useMemo(
    () => (view && PRICE_VIEW_PAGE[view] === 'daily' ? (c: Card) => cardMatchesView(view, c, pendingConfirmIds) : null),
    [view, pendingConfirmIds],
  );
  // 마지막 가격 수집 시각(결정 D10 — 6시간 넘으면 경고). 카드를 한 번도 못 받았으면 줄을 그리지 않는다.
  const collectedAt = useMemo(
    () => (cards.hasLoadedOnce ? lastCollectedAt(cards.cards) : undefined),
    [cards.hasLoadedOnce, cards.cards],
  );

  // "매장가 요청" 화면은 늘 PriceHub 변경 표다(예전 탭을 연 상태와 같다).
  const onHubTab = storePrice;

  // "매장가 요청" 의 선택 — 이 화면에서 사용자가 체크한 카드만(감사 card-controltower#330, 사장 제보 2026-10-29). 예전엔
  // ① 공용 selection(CardPageContext — 가격 조회의 ① 시작 · 끝이 상승 · 하락 전체로 채운다)을 같이 썼고 ② 들어올 때 · 목록을
  // 다시 받을 때 상승 · 하락을 기본 선택으로 채웠다 — 아무것도 고르지 않았는데 체크된 줄 · "다른 페이지 84건 선택" 이 떠 있어
  // 그대로 "네이버 반영" 을 누르면 의도치 않은 가격 변경이 나갈 수 있었다. 이제 선택은 이 화면의 상태(메모리)로 따로 두고:
  //   - 들어오면(메뉴 이동 · 브라우저 새로고침 · SUPER 매장 전환으로 다시 마운트) 0건
  //   - 매장이 바뀌면(SUPER 매장 · 다른 매장 목록) 비운다 · 표의 필터를 바꾸면 비운다(HubChangesTable)
  //   - 목록을 다시 받으면(잡 끝 · 새로고침) 고른 것 중 지금 체크할 수 있는 것만 남기고 새 줄을 더하지 않는다
  // 나눠 보내다 멈춘 카드 · 결과 불명 카드는 체크가 아니라 표의 "다시 시도" 줄 · 줄 잠금으로 따로 남는다(선택과 무관).
  const [hubPicked, setHubPicked] = useState<number[]>([]);
  useEffect(() => {
    setHubPicked((prev) => (prev.length === 0 ? prev : []));
  }, [storePrice, superStore, username]);
  const hubStoreRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!onHubTab || !hub.data) return;
    const store = hub.data.store ?? null;
    const prevStore = hubStoreRef.current;
    hubStoreRef.current = store;
    if (prevStore !== undefined && prevStore !== store) {
      setHubPicked((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    // 고른 것 중 지금 체크할 수 있는 것만 남긴다(감사 #73 — 받을 때마다 고른 선택을 덮지 않는 것은 그대로). 하락 확인 대기는
    // 요청은 되므로 남기고 ③ 에서만 뺀다(감사 #332).
    const { selectableSet } = hubModel;
    setHubPicked((prev) => {
      const kept = prev.filter((id) => selectableSet.has(id));
      return kept.length === prev.length ? prev : kept;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onHubTab, hub.data]);

  // 이 화면의 요청 대상 = 고른 카드 ∩ 체크 가능한 카드(확정 대기 · CT 에 없는 상품은 들어갈 수 없다).
  const hubSelectedIds = useMemo(
    () => hubPicked.filter((id) => hubModel.selectableSet.has(id)),
    [hubPicked, hubModel.selectableSet],
  );
  // 네이버 가격 → PriceHub 매장가 적용 뒤 — 서버가 수정가 · PriceHub 값을 현재가로 맞췄으니(경고 없는 것만) 목록을
  // 다시 받기 전에도 카드 값을 같게 맞추고, 적용한 카드는 선택에서 뺀다(그대로 두면 하단 ③ 이 다시 보낸다).
  // 하락 확인 대기였던 카드는 UNCHANGED 가 됐으니 대기 목록(저장분 포함)에서도 뺀다 — 남기면 검토 목록에서 사라진 카드가
  // 대기 건수에 남아 다른 탭의 ③ 전체 반영을 막는다(코덱 리뷰 #105 중간).
  // 서버가 경고 없이 맞춘 카드는 화면의 현재가가 그 사이 달라졌어도(조회 시차) 보낸 값으로 맞추고 대기에서 뺀다 — 서버 확정값이
  // 기준이다(Opus 리뷰 PR #43 낮음).
  const onAppliedCurrent = useCallback((applied: ApplyCurrentApplied[]) => {
    const byId = new Map(cards.cards.map((c) => [c.id, c]));
    const patched: Card[] = [];
    const settled: number[] = [];
    applied.forEach((a) => {
      // 요청 대기열(감사 card-controltower#71) — 요청(확정 대기)한 카드는 매장가 · 수정가가 그대로라 카드를 '적용됨' 으로
      // 바꾸지 않고 하락 확인 대기도 남긴다(PriceHub 관리자가 확정하기 전까지 ③ 에서 빠지는 것은 서버 · 표가 맡는다).
      if (isPendingApplied(a)) return;
      if (a.warning) return;
      settled.push(a.cardId);
      const c = byId.get(a.cardId);
      if (!c) return;
      patched.push({
        ...c, currentPrice: a.hubPrice, suggestedPrice: a.hubPrice, hubPrice: a.hubPrice,
        priceChangeStatus: 'UNCHANGED', priceChangeRate: 0, manualOverrideAt: null,
      });
    });
    if (patched.length > 0) cards.mergeResults(patched);
    if (settled.length > 0) edit.removePendingMany(settled);
    const done = new Set(applied.map((a) => a.cardId));
    setHubPicked((prev) => prev.filter((id) => !done.has(id)));
  }, [cards, edit]);

  // 오른쪽 위 건수 문장(상승 · 하락 · 알림 · 확정 대기 N건)은 없앴다(설계 2026-11-04 §3 — 보기 칩 건수가 같은 말).
  const viewBar = (
    <PriceViewBar page={pageKey} view={view} counts={viewCounts} onChange={setView} collectedAt={collectedAt}
      countNotes={countNotes} />
  );

  // 재조회만 실패하고 이전 조회 결과가 남아 있으면 표를 통째로 오류 화면으로 덮지 않고 위에 한 줄로 알린다
  // (StoreProductManagementPage와 같은 inline 변형, 2026-10-19 리뷰).
  // "매장가 요청" 은 카드 탭이 없어 첫 조회 실패도 이 한 줄로 알린다 — PriceHub 변경 표는 따로 받아 그대로 보인다(감사 #78).
  const cardsInlineError = cards.error && (!failedEmpty || storePrice)
    ? <AppLoadError inline message={cards.error.message} error={cards.error} onRetry={cards.retry} />
    : null;

  return (
    <DashboardLayout title={storePrice ? '매장 가격 요청' : '가격 조회'}>
      {storePrice ? (
        <>
          {/* ③ 은 여기서 고른 카드만 보낸다(예전 PriceHub 변경 탭의 선택 모드 그대로). */}
          {/* 분류 요약 · 하락 확인 현황은 가격 조회 화면으로 옮겼다(F2 #452). */}
          {/* 블록 사이 16(.ct-stack) · 표 화면 순서 도구줄 → 표 → 쪽 넘김(.ct-table-page) — CT 배치 규칙 L2 템플릿 C. */}
          <div className="ct-stack">
          {cardsInlineError}
          <div className="ct-table-page" data-ds="table-page">
          {viewBar}
          <HubChangesTable
            data={hub.data}
            rows={hubModel.rows}
            loading={hub.loading}
            error={hub.error}
            onReload={reloadHubAndCards}
            dataStartedAt={hub.dataStartedAt}
            selectedIds={hubSelectedIds}
            onSelectedIdsChange={setHubPicked}
            onAppliedCurrent={onAppliedCurrent}
            scrollY={HUB_TABLE_SCROLL_Y}
            statusTab={hubTabOfView(view)}
            onStatusTabChange={(tab) => setView(viewOfHubTab(tab))}
          />
          </div>
          </div>
        </>
      ) : (
        <>
          {/* ①②③ 세 단계 → 시세 분류 요약(① 의 결과 · ② 의 입력, 접기) → 하락 확인 현황 → 카드 탭 표(F2 #452).
              블록 사이 간격은 .ct-stack 이 준다(16, 블록은 바깥 margin 없음) — CT 배치 규칙 L2. */}
          <div className="ct-stack">
          <DailyWorkflowBar />
          {/* 07:30 서버 체인 결과(감사 #566, 백엔드 PR #78) — 옛 서버면 그리지 않는다. */}
          <MorningSummaryCard />
          {cardsInlineError}
          {/* 접을 수 있다(감사 #326). 접힘은 사용자별로 남는다. "목록 보기" 는 아래 표의 그 탭으로. */}
          <DropClassificationPanel onShowTab={showTab} collapseKey={dropPanelCollapsedKey(isLoggedIn ? username : null)}
            onShowDrops={showDrops} />
          {/* 표 화면(템플릿 C .ct-table-page) — 도구줄(보기 칩 · 신선도) → 탭 · 표 → 쪽 넘김. 칩 · 요약의 '목록 보기' 가 여기로 내려온다. */}
          <div ref={tableAnchorRef} className="ct-table-page" data-ds="table-page">
          {viewBar}
          {/* 첫 조회부터 실패하면 오류 화면은 카드 탭 자리에(감사 card-controltower#78). */}
          <DropReviewTable
            tab={tableTab}
            onTabChange={setTableTab}
            scrollY={TABLE_SCROLL_Y}
            cardTabsFallback={failedEmpty
              ? <AppLoadError message={cards.error?.message} error={cards.error} onRetry={cards.retry} />
              : undefined}
            viewFilter={dropViewFilter}
            viewLabel={view ? PRICE_VIEW_LABEL[view] : undefined}
            onClearView={() => setView(null)}
            focus={lookupFocus}
            onFocusClose={() => setLookupFocus(null)}
          />
          </div>
          </div>
        </>
      )}

      <JobModal
        job={resultJob}
        open={resultModalOpen}
        onClose={() => setResultModalOpen(false)}
        onGoToDropReview={goToDropReview}
      />

      <DropReviewModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
      />
    </DashboardLayout>
  );
};

/** "매장가 요청" 메뉴(감사 card-controltower#117) — 가격 조회 화면과 같은 논리의 PriceHub 변경 · 분류 카드 쪽. */
export const StorePriceRequestPage: React.FC = () => <DailyWorkflowPage variant="storePrice" />;
